import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToSection } from "@/hooks/useLenis";
import { IMAGES } from "@/data/content";

const EASE = [0.25, 0.1, 0.25, 1];

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.8]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-screen min-h-[640px] w-full overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img
          src={IMAGES.hero}
          alt={IMAGES.heroAlt}
          className="w-full h-full object-cover object-center"
          fetchPriority="high"
          decoding="async"
          width={1536}
          height={1024}
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-[#1A1A1A]/20" />

      <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="block text-xs sm:text-sm font-medium tracking-[0.32em] uppercase text-[#E9D9BE] mb-7"
          >
            {t.hero.overline}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.32 }}
            className="font-display text-[#FAF9F6] text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.92] tracking-tight"
          >
            Olivier Boës
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
            className="font-display text-[#E9D9BE] text-2xl sm:text-3xl lg:text-4xl font-light italic mt-3 mb-7"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.62 }}
            className="text-[#FAF9F6]/85 text-base sm:text-lg font-light leading-relaxed max-w-xl mb-10"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.74 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToSection("contact")}
              className="px-9 py-4 bg-[#FAF9F6] text-[#1A1A1A] text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#C5A880] hover:text-[#FAF9F6] transition-colors duration-300"
            >
              {t.hero.cta}
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToSection("gallery")}
              className="px-9 py-4 border border-[#FAF9F6]/60 text-[#FAF9F6] text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#FAF9F6] hover:text-[#1A1A1A] transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToSection("about")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#FAF9F6]/70 hover:text-[#FAF9F6]"
        aria-label="Scroll down"
      >
        <span className="animate-bounce inline-block"><ArrowDown size={22} /></span>
      </motion.button>
    </section>
  );
};
